import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * A lightbox dialog for an installation video.
 *
 * Extends {@link DialogComponent} to lazily mount the video iframe when the
 * dialog opens and remove it when the dialog closes. This keeps the external
 * player (YouTube/Vimeo) from loading on initial page render and — crucially —
 * stops playback when the dialog is dismissed (an iframe left in the DOM would
 * keep playing audio).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [player] - Container the iframe is mounted into.
 * @property {HTMLTemplateElement} [template] - Template holding the iframe markup.
 *
 * @extends DialogComponent
 */
class VideoLightbox extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#unmount);
  }

  /**
   * Mounts the iframe before opening so it starts loading as the dialog appears.
   */
  showDialog() {
    this.#mount();
    super.showDialog();
  }

  #mount = () => {
    const { player, template } = this.refs;
    if (!player || !template || player.childElementCount > 0) return;
    player.appendChild(template.content.cloneNode(true));
  };

  #unmount = () => {
    const { player } = this.refs;
    if (player) player.replaceChildren();
  };
}

if (!customElements.get('video-lightbox')) customElements.define('video-lightbox', VideoLightbox);
